import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';

// REDUX
import { useDispatch } from 'react-redux';
import { ToastMessagesSlice } from '../../store/slice/ToastMessages';
import { useAppSelector } from '../../store/slice';

// PACKAGES
import { Field, Formik, Form as FormikForm, FormikHelpers } from 'formik';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
	Accordion,
	AccordionBody,
	AccordionHeader,
	AccordionItem,
	Dropdown,
	Modal,
	Offcanvas,
	OverlayTrigger,
	Spinner,
	Tooltip
} from 'react-bootstrap';

// COMPONENTS
import TitleSection from '../../components/TitleSection/TitleSection';
import SvgMask from '../../components/_Helpers/SvgMask';
import Divider from '../../components/Divider/Divider';
import FormCustomError from '../../components/Forms/FormCustomError';
import FormGroupWithError from '../../components/Forms/FormGroupWithError';

// UTILS
import CroppedImage from '../../components/_Helpers/CroppedImage';
import { createErrorsObject, guid, inlineWait } from '../../libs/utils';
import ImgRem from '../../libs/imgRem';
import DelayedFadeIn from '../../components/_Helpers/DelayedFadeIn';
import { AnalyticsPushBooksAssignedToStudents } from '../../components/_Helpers/AnalyticsEvents';

// TYPES
import apibridge from '../../apibridge';
import {
	BookListListResult,
	BookStagesStage,
	ClassListClass,
	StudentAdminEditCommand,
	StudentAdminViewViewResult
} from '../../api/models';
import { UserSlice, UserType } from '../../store/slice/User';
import { ResponseStandardFail } from '../../libs/api';
import { DynamicFtux, getStudentDetailsConfig, useDynamicFtuxRestart, useFtuxHighlight } from '../../components/Ftux';

const Student: FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [, , , studentId] = location.pathname.split('/');

	const { state } = location;
	const [loading, setLoading] = useState(true);

	const [profileDetails, setProfileDetails] = useState<StudentAdminViewViewResult>({
		classId: state?.classId || '', // the class id they entered the page with: '' for archived, or a class id otherwise
		className: state?.className || '',
		firstName: state?.studentDetails?.firstName || '',
		lastName: state?.studentDetails?.lastName || ''
	});
	const [selectedBookStageId, setSelectedBookStageId] = useState('');
	const [classList, setClassList] = useState<ClassListClass[]>();
	const [selectedClassId, setSelectedClassId] = useState('');
	const [bookStages, setBookStages] = useState<BookStagesStage[]>();
	const [activeStageName, setActiveStageName] = useState('');
	const [isAssigningBooks, setIsAssigningBooks] = useState(false);

	const [showArchiveModal, setShowArchiveModal] = useState(false);
	const [showChangeClassModal, setShowChangeClassModal] = useState(false);
	const [showAssignBookModal, setShowAssignBookModal] = useState(false);
	const [showAssignBookSpecificStageModal, setShowAssignBookSpecificStageModal] = useState(false);
	const [showConfirmAssignBookModal, setShowConfirmAssignBookModal] = useState(false);
	const [showUnassignStageModal, setShowUnassignStageModal] = useState(false);
	const [showUnassignBookModal, setShowUnassignBookModal] = useState(false);
	const [bookIdPendingUnassign, setBookIdPendingUnassign] = useState('');
	const [showDeleteStudentModal, setShowDeleteStudentModal] = useState(false);
	const [showEditStudentNameModal, setShowEditStudentNameModal] = useState(false);
	const [bookList, setBookList] = useState<BookListListResult>();

	const activeStageDetails = profileDetails?.stages?.find((stage) => stage.name === activeStageName);
	const studentIsArchived = !profileDetails?.className;

	const selectedBookStageBooksCount = bookStages?.find((s) => s.id === selectedBookStageId)?.books || '';

	const user: UserType = useAppSelector((state) => state.user);

	const studentDetailsConfig = useMemo(() => getStudentDetailsConfig(), []);
	const shouldHighlightStudentDetailsView = useFtuxHighlight('student-details-view', 'student-details-ftux');
	const shouldHighlightStudentMenu = useFtuxHighlight('student-menu', 'student-details-ftux');
	const shouldHighlightStudentTab = useFtuxHighlight('student-tab', 'student-details-ftux');
	const shouldHighlightStageBooks = useFtuxHighlight('stage-books-0', 'student-details-ftux');
	const shouldHighlightStageBooksArrow = useFtuxHighlight('stage-books-arrow', 'student-details-ftux');
	const shouldHighlightManageBookButton = useFtuxHighlight('manage-book-button', 'student-details-ftux');

	const { restartTutorial, manualRestartTrigger } = useDynamicFtuxRestart('StudentDetail');

	const getProfileDetails = async () => {
		const response = await apibridge.getStudentAdminView(studentId);
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setProfileDetails(response.data.result);
				if (!activeStageName && response.data.result.stages?.[0].name)
					setActiveStageName(response.data.result.stages?.[0].name);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Profile error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}
	};

	const getClassList = async () => {
		// get class list
		const response = await apibridge.getClassList();
		if (response && response.data && !response.data.isError && response.data.result) {
			setClassList(response.data.result.classes || []);
		}
	};

	const getBookStages = async () => {
		const response = await apibridge.getBookStages();
		if (response && response.data && !response.data.isError && response.data.result) {
			setBookStages(response.data.result.stages || []);
			if (!activeStageName && response.data.result.stages?.[0].name)
				setActiveStageName(response.data.result.stages?.[0].name);
		}
	};

	const initialise = async () => {
		await Promise.all([getBookStages(), getProfileDetails(), getClassList()]);
		setLoading(false);
	};

	// Assign books
	const handleAssignBooks = async () => {
		setIsAssigningBooks(true);

		const response = await apibridge.postStudentAdminBookAssign({
			studentProfileIds: [studentId],
			stageIds: [selectedBookStageId],
			bookIds: [],
			seriesIds: []
		});
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				getProfileDetails();
				setSelectedBookStageId('');

				// need to get data for every book so can pass to analytics
				let bookListData = bookList;
				if (!bookListData) {
					const bookListResponse = await apibridge.getBookList();
					if (
						bookListResponse &&
						bookListResponse.data &&
						!bookListResponse.data.isError &&
						bookListResponse.data.result
					) {
						setBookList(bookListResponse.data.result);
						bookListData = bookListResponse.data.result;
					}
				}

				// for assigning a stage to a student, need to know every book name and ID being assigned
				if (bookListData) {
					AnalyticsPushBooksAssignedToStudents({
						user,
						bookData: {
							books:
								bookListData?.stages?.flatMap((stage) => {
									const { serieses = [] } = stage;
									return serieses.flatMap((series) => {
										const { books = [] } = series;
										return books.map((book) => ({
											book_name: book.name || '',
											book_id: book.id || ''
										}));
									});
								}) || []
						},
						studentData: [{ studentId }]
					});
				}

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Book assignment',
						description: 'Books successfully assigned.'
					})
				);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Book assignment error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsAssigningBooks(false);
		setShowConfirmAssignBookModal(false);
	};

	// Change class
	const handleChangeClass = async () => {
		const response = await apibridge.postStudentAdminClassSwap({
			classId: selectedClassId,
			profileIds: [studentId]
		});
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				const className = classList?.find((classItem) => classItem.id === selectedClassId)?.name || '';
				const description = `${profileDetails?.firstName || ''} ${
					profileDetails?.lastName || ''
				} has been transferred to ${className}`;

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: `Class transfer complete`,
						description: description
					})
				);

				// setSelectedClassId(classId); // but why?
				getProfileDetails();
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Change class error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setShowChangeClassModal(false);
	};

	// Change class for archived student
	const handleUnarchiveStudent = async () => {
		const response = await apibridge.postStudentAdminUnarchive({
			classId: selectedClassId,
			profileIds: [studentId]
		});

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				const className = classList?.find((classItem) => classItem.id === selectedClassId)?.name || '';
				const description = `${profileDetails?.firstName || ''} ${
					profileDetails?.lastName || ''
				} has been moved to ${className}`;

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: `Student assigned to class`,
						description: description
					})
				);

				// setSelectedClassId(selectedClassId); // but why?
				getProfileDetails();
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Change class error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setShowChangeClassModal(false);
	};

	// Archive
	const handleArchiveStudent = async () => {
		const response = await apibridge.deleteStudentAdminArchive({ profileIds: [studentId] });
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				const description = `${profileDetails?.firstName || ''} ${
					profileDetails?.lastName || ''
				} has been moved to Archive`;

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: `Student archived`,
						description: description
					})
				);

				navigate(`/student-management/class/${profileDetails.classId}`);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Archive student error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}
		setShowArchiveModal(false);
	};

	const handleDeleteStudent = async () => {
		const response = await apibridge.deleteStudentAdminDelete({ profileIds: [studentId] });
		if (response && response.data) {
			if (!response.data.isError) {
				const description = `${profileDetails?.firstName || ''} ${profileDetails?.lastName || ''} has been deleted`;

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: `Student deleted`,
						description: description
					})
				);

				navigate(`/student-management/class/${profileDetails.classId}`);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Delete student error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}
	};

	// Unassign stage
	const handleUnassignStage = async () => {
		const response = await apibridge.postStudentAdminBookUnassign({
			studentProfileId: studentId,
			stageIds: [activeStageDetails?.id || '']
		});
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				getProfileDetails();

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Book assignment',
						description: 'All books in stage successfully unassigned.'
					})
				);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Unassign stage error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}
		setShowUnassignStageModal(false);
	};

	const handleUnassignBook = async (bookId: string) => {
		const response = await apibridge.postStudentAdminBookUnassign({
			studentProfileId: studentId,
			bookIds: [bookId || '']
		});
		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				getProfileDetails();

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Book assignment',
						description: 'Book successfully unassigned.'
					})
				);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Unassign book error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}
	};

	useEffect(() => {
		initialise();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// EDIT NAME
	const [editNameValues, setEditNameValues] = useState<StudentAdminEditCommand>({
		firstName: profileDetails?.firstName || '',
		lastName: profileDetails?.lastName || ''
	});

	// update editNameValues when profileDetails changes
	useEffect(() => {
		setEditNameValues({
			firstName: profileDetails?.firstName || '',
			lastName: profileDetails?.lastName || ''
		});
	}, [profileDetails]);

	const [editNameSubmitting, setEditNameSubmitting] = useState(false);

	const validateEditName = (values: StudentAdminEditCommand) => {
		const errors: { [field: string]: string } = {};
		if (values['firstName'] === profileDetails?.firstName && values['lastName'] === profileDetails?.lastName) {
			errors.sameName = 'Same full name entered. Please enter a new name to change it.';
		}

		return errors;
	};

	const postEditStudentName = async (
		values: StudentAdminEditCommand,
		formikHelpers: FormikHelpers<StudentAdminEditCommand>
	) => {
		setEditNameSubmitting(true);

		const { firstName, lastName } = values;

		const response = await apibridge.postStudentAdminEdit({
			profileId: studentId,
			firstName,
			lastName
		});

		if (response && response.data) {
			if (response.data.isError) {
				const errsObj = createErrorsObject(response.data as ResponseStandardFail);
				formikHelpers.setErrors(errsObj);
			} else if (response.data.result) {
				setShowEditStudentNameModal(false);

				// re-fetch the user details and update <ToastNag />
				dispatch(UserSlice.actions.forceUpdate());

				// Wait till user details are re-fetched then show the toast, otherwise it will flash on screen
				await inlineWait(500);

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: `Student name changed to ${firstName} ${lastName}`,
						description: ''
					})
				);
			}
		}

		setEditNameSubmitting(false);
	};

	return (
		<div className="student-details-page d-flex flex-column flex-grow-1">
			{loading ? (
				<div className="d-flex align-items-center justify-content-center flex-grow-1">
					<DelayedFadeIn>
						<Spinner animation="border" role="status">
							<span className="visually-hidden">Loading...</span>
						</Spinner>
					</DelayedFadeIn>
				</div>
			) : (
				<>
					<TitleSection
						isHelpSection={true}
						onHelpClick={() => restartTutorial()}
						backToText={studentIsArchived ? 'Back to student archive' : 'Back to student list'}
						backToUrl={
							studentIsArchived
								? '/student-management/archived-students'
								: `/student-management/class/${profileDetails.classId}`
						}
						dynamicTitle={
							<div className="d-flex align-items-center gap-3-5">
								<h1 className="text-shades-800 title m-0">
									<strong>
										{profileDetails?.firstName || ''} {profileDetails?.lastName || ''}
									</strong>
								</h1>
								{user.permissions?.some((permission) =>
									(studentIsArchived
										? ['StudentAdminUnarchive', 'StudentAdminDelete']
										: ['StudentAdminBookAssign', 'StudentAdminClassSwap', 'StudentAdminArchive']
									).includes(permission)
								) && (
									<Dropdown>
										<OverlayTrigger placement="top" overlay={<Tooltip>More</Tooltip>}>
											<Dropdown.Toggle
												id="student-menu"
												className={`btn btn-sm btn-icon btn-dropdown hovered-style rotate-90 ${
													shouldHighlightStudentMenu ? 'highlight-outline' : ''
												}`}
											>
												<SvgMask path="/svg/ellipsis-v.svg" width={24} height={24} />
											</Dropdown.Toggle>
										</OverlayTrigger>
										<Dropdown.Menu>
											{studentIsArchived ? (
												<>
													{user.permissions?.includes('StudentAdminUnarchive') && (
														<Dropdown.Item as="button" onClick={() => setShowChangeClassModal(true)}>
															Assign to class
														</Dropdown.Item>
													)}
													{user.permissions?.includes('StudentAdminDelete') && (
														<Dropdown.Item
															as="button"
															onClick={() => setShowDeleteStudentModal(true)}
															className="option-destructive"
														>
															Delete student
														</Dropdown.Item>
													)}
												</>
											) : (
												<>
													{user.permissions?.includes('StudentAdminClassSwap') && (
														<Dropdown.Item as="button" onClick={() => setShowChangeClassModal(true)}>
															Change class
														</Dropdown.Item>
													)}
													{user.permissions?.includes('StudentAdminEdit') && (
														<Dropdown.Item as="button" onClick={() => setShowEditStudentNameModal(true)}>
															Edit student name
														</Dropdown.Item>
													)}
													{user.permissions?.includes('StudentAdminArchive') && (
														<Dropdown.Item
															as="button"
															onClick={() => setShowArchiveModal(true)}
															className="option-destructive"
														>
															Archive student
														</Dropdown.Item>
													)}
												</>
											)}
										</Dropdown.Menu>
									</Dropdown>
								)}
							</div>
						}
						dynamicDescription={
							<div
								id="student-details-view"
								className={`d-flex align-items-center gap-3-5 ${
									shouldHighlightStudentDetailsView ? 'highlight-outline rounded-3' : ''
								}`}
							>
								<div className="d-flex align-items-center gap-2">
									<SvgMask path="/svg/class.svg" width={24} height={24} />
									<p className="mb-0">
										<strong>{studentIsArchived ? 'Archived' : profileDetails?.className}</strong>
									</p>
								</div>
								<div className="vr text-shades-400"></div>
								<p className="mb-0">
									<strong>
										<span className="text-shades-500 me-2">Username:</span>
										{profileDetails?.username}
									</strong>
								</p>
								<div className="vr text-shades-400"></div>
								<p className="mb-0">
									<strong>
										<span className="text-shades-500 me-2">Password:</span>
										{profileDetails?.password}
									</strong>
								</p>
							</div>
						}
					>
						<div className="d-flex justify-content-between align-items-end gap-2">
							{/* Stages */}
							<div
								id="student-tab"
								className={`d-flex align-items-end gap-1 stage-tab-wrapper ${
									shouldHighlightStudentTab ? 'highlight-outline rounded-3' : ''
								}`}
							>
								{bookStages?.map((stage) => {
									const { id = '', name = '' } = stage;
									const stageIsAssigned = profileDetails?.stages?.some((s) => s.name === name);
									const stageIsActive = stage.name === activeStageName;
									return (
										<button
											key={id}
											onClick={() => setActiveStageName(name)}
											className={`btn stage-tab d-flex align-items-center gap-2 ${
												stageIsActive ? `active` : ''
											} bg-stage-${name} ${stageIsAssigned ? 'assigned' : ''}`}
										>
											<p className="mb-0">
												<strong>{name}</strong>
											</p>
										</button>
									);
								})}
							</div>
							<button
								id="manage-book-button"
								onClick={() => setShowAssignBookModal(true)}
								className={`btn btn-primary mb-3 ${shouldHighlightManageBookButton ? 'highlight-outline' : ''}`}
							>
								Manage books <SvgMask path="/svg/book-az.svg" height={16} width={16} />
							</button>
						</div>
					</TitleSection>

					<div
						className={`container content d-flex flex-column flex-grow-1 gap-3-5 stage-${
							activeStageDetails?.name || activeStageName
						}`}
					>
						{/* Title Bar */}
						<div className={`d-flex align-items-center justify-content-between title-bar `}>
							<h2>
								<strong>Stage {activeStageDetails?.name || activeStageName}</strong>
							</h2>
							{studentIsArchived ? null : activeStageDetails?.id ? (
								!showUnassignStageModal ? (
									<button
										type="button"
										onClick={() => setShowUnassignStageModal(true)}
										className="btn btn-white btn-title-unassign"
									>
										Unassign all books in Stage <SvgMask path="/svg/book-remove.svg" height={16} width={16} />
									</button>
								) : (
									<button
										type="button"
										aria-pressed={true}
										onClick={() => setShowUnassignStageModal(false)}
										className="btn btn-white btn-title-unassign btn-title-unassign-active"
									>
										Unassign all books in Stage
										<SvgMask path="/svg/book-remove.svg" height={16} width={16} />
									</button>
								)
							) : (
								<button
									onClick={() => setShowAssignBookSpecificStageModal(true)}
									className="btn btn-white btn-title-assign"
								>
									Assign Books <SvgMask path="/svg/book-add.svg" height={16} width={16} />
								</button>
							)}
						</div>

						{activeStageDetails?.serieses?.length ? (
							activeStageDetails.serieses.map((series, index) => (
								<div
									key={series.id}
									className={`series-row ${shouldHighlightStageBooks && index === 0 ? 'highlight-outline' : ''}`}
									{...(shouldHighlightStageBooks && index === 0 && { id: 'stage-books-0' })}
								>
									<h3 className="series-title py-3 px-4 text-shades-800 m-0 align-content-center">
										<strong>{series.name}</strong>
									</h3>
									<div className="booklist">
										<Accordion className="w-100 pb-2 px-2 d-flex flex-column gap-2" alwaysOpen>
											{series.books?.map((book, bookIndex) => (
												<div key={book.id} className="d-flex gap-2">
													<AccordionItem
														eventKey={book.id || ''}
														className={`d-flex flex-grow-1 flex-column ${!book.activities?.length ? 'disabled' : ''} ${
															shouldHighlightStageBooksArrow && index === 0 && bookIndex === 0
																? 'highlight-box rounded-3'
																: ''
														}`}
														{...(shouldHighlightStageBooksArrow &&
															index === 0 &&
															bookIndex === 0 && { id: 'stage-books-arrow' })}
													>
														<AccordionHeader>
															<div className="accordion-header-custom-width-alignment">
																<div className="row row-gap-3">
																	<div className="col-6 col-lg">
																		<div className="d-flex align-items-center gap-3-5 h-100 book-title">
																			<div className="image-or-text-start-wrapper">
																				{book.coverUrl && (
																					<CroppedImage
																						src={book.coverUrl}
																						width={48}
																						height={60}
																						className="book-image rounded-1 object-fit-contain flex-shrink-0"
																						alt=""
																					/>
																				)}
																			</div>
																			<strong>{book.name}</strong>
																		</div>
																	</div>
																	<div className="col-6 col-lg">
																		<div
																			className={`d-flex align-items-center gap-2 h-100 progress-${
																				book.status ? book.status?.toLowerCase() : book.status
																			}`}
																			style={{ '--progress-variable': `${book.progress}` } as React.CSSProperties}
																		>
																			<span
																				className={`status-icon ${
																					book.status === 'InProgress' ? 'status-icon-dynamic' : ''
																				}`}
																			></span>

																			<strong>
																				{book.status === 'Pending'
																					? 'Not started'
																					: book.status === 'InProgress'
																						? 'In progress'
																						: book.status}
																			</strong>
																		</div>
																	</div>
																	<div className="col-6 col-lg">
																		<div className="d-flex align-items-center gap-2 h-100 activity-score">
																			{book.activityScore ? (
																				<strong>
																					<span className="text-shades-500 me-2">Activity Score:</span>
																					{book.activityScore}%
																				</strong>
																			) : null}
																		</div>
																	</div>
																	<div className="col-6 col-lg">
																		<div className="d-flex align-items-center gap-2 h-100">
																			<p className="text-shades-500 mb-0">
																				<strong>Rating:</strong>
																			</p>
																			<div className="d-flex align-items-center gap-1">
																				{book.emotion === 'Happy' ? (
																					<ImgRem src="/svg/happy.svg" height={24} width={24} alt="Happy face" />
																				) : (
																					<span className="dot"></span>
																				)}
																				{book.emotion === 'Confused' ? (
																					<ImgRem src="/svg/okay.svg" height={24} width={24} alt="OK face" />
																				) : (
																					<span className="dot"></span>
																				)}
																				{book.emotion === 'Unhappy' ? (
																					<ImgRem src="/svg/sad.svg" height={24} width={24} alt="Unhappy face" />
																				) : (
																					<span className="dot"></span>
																				)}
																			</div>
																		</div>
																	</div>
																</div>
															</div>
														</AccordionHeader>

														<AccordionBody>
															{book.activities?.map((activity, activityIndex) => (
																<div key={activityIndex} className="activity-wrapper d-flex align-items-center">
																	<div className="accordion-body-custom-width-alignment">
																		<div key={activity.id} className="row row-gap-3 row-activity">
																			<div className="col-12 col-lg-6">
																				<div className="d-flex align-items-center gap-3-5 h-100">
																					<div className="image-or-text-start-wrapper">
																						<p className="mb-0 text-vivid-blue">
																							<strong>Q{activityIndex + 1}.</strong>
																						</p>
																					</div>
																					<p className="mb-0 activity-name">
																						<strong>{activity.name}</strong>
																					</p>
																				</div>
																			</div>
																			<div className="col-6 col-lg">
																				<div
																					className={`d-flex align-items-center gap-2 h-100 status-bar ${
																						activity.status === 'Successful'
																							? 'correct'
																							: activity.status === 'Unsuccessful'
																								? 'incorrect'
																								: ''
																					}`}
																				>
																					{activity.status && activity.status !== 'InProgress' ? (
																						<div className="activity-status-icon"></div>
																					) : null}
																					<p className="mb-0">
																						<strong>
																							{activity.status === 'Successful'
																								? 'Correct'
																								: activity.status === 'Unsuccessful'
																									? 'Incorrect'
																									: ''}
																						</strong>
																					</p>
																				</div>
																			</div>
																			<div className="col-6 col-lg">
																				<div className="d-flex align-items-center h-100">
																					<p className="mb-0">
																						{activity.attemptsCount !== 0 ? (
																							<strong>
																								<span className="text-shades-500 me-2">Attempts:</span>
																								{activity.attemptsCount}
																							</strong>
																						) : null}
																					</p>
																				</div>
																			</div>
																		</div>
																	</div>
																</div>
															))}
														</AccordionBody>
													</AccordionItem>
													{studentIsArchived ? null : !showUnassignBookModal ? (
														<OverlayTrigger
															placement="top"
															overlay={
																<Tooltip id={`unassign-book-${book.id || `idx-${bookIndex}`}`}>Unassign book</Tooltip>
															}
														>
															<button
																type="button"
																aria-label="Unassign book"
																onClick={() => {
																	setBookIdPendingUnassign(book.id || '');
																	setShowUnassignBookModal(true);
																}}
																className="btn btn-unassign"
															>
																<SvgMask path="/svg/book-remove.svg" width={24} height={24} />
															</button>
														</OverlayTrigger>
													) : (
														<button
															type="button"
															aria-label="Unassign book"
															aria-pressed={bookIdPendingUnassign === (book.id || '')}
															onClick={() => {
																setBookIdPendingUnassign(book.id || '');
																setShowUnassignBookModal(false);
															}}
															className={`btn btn-unassign${
																bookIdPendingUnassign === (book.id || '') ? ' btn-unassign-active' : ''
															}`}
														>
															<SvgMask path="/svg/book-remove.svg" width={24} height={24} />
														</button>
													)}
												</div>
											))}
										</Accordion>
									</div>
								</div>
							))
						) : (
							<div className="d-flex align-items-center justify-content-center empty-state">
								<SvgMask path="/svg/book-add.svg" width={16} height={16} />
								<p className="mb-0">No books in Stage assigned.</p>
							</div>
						)}
					</div>
					{/* Archive student modal */}
					<Modal show={showArchiveModal} onHide={() => setShowArchiveModal(false)} centered>
						<Modal.Body>
							<h2 className="m-0 text-shades-800">
								<strong>Archive {profileDetails?.firstName + ' ' + profileDetails?.lastName}?</strong>
							</h2>
							<p className="m-0">
								This will move this student to <strong>Archived students.</strong> <br /> <br /> Archived students will
								be removed from the platform after 30 days of their archival. Before this, you can reassign them to
								another class.
							</p>
							<div className="button-group d-flex">
								<button onClick={() => setShowArchiveModal(false)} className="btn btn-lg btn-white w-50">
									Cancel
								</button>
								<button onClick={handleArchiveStudent} className="btn btn-lg w-50">
									Archive student
								</button>
							</div>
						</Modal.Body>
					</Modal>
					{/* Unassign book modal */}
					<Modal
						show={showUnassignBookModal}
						onHide={() => {
							setShowUnassignBookModal(false);
							setBookIdPendingUnassign('');
						}}
						centered
					>
						<Modal.Body>
							<h2 className="m-0 text-shades-800">
								<strong>Unassign book?</strong>
							</h2>
							<p className="body-normal m-0">
								This will remove the book from the student&apos;s eLibrary and <strong>reset progress</strong> for the
								currently assigned book.
							</p>
							<div className="button-group d-flex">
								<button
									type="button"
									onClick={() => {
										setShowUnassignBookModal(false);
										setBookIdPendingUnassign('');
									}}
									className="btn btn-lg btn-white w-50"
								>
									Cancel
								</button>
								<button
									type="button"
									onClick={async () => {
										if (bookIdPendingUnassign) {
											await handleUnassignBook(bookIdPendingUnassign);
										}
										setShowUnassignBookModal(false);
										setBookIdPendingUnassign('');
									}}
									className="btn btn-lg btn-destructive w-50"
								>
									Unassign book
								</button>
							</div>
						</Modal.Body>
					</Modal>
					{/* Delete student modal */}
					<Modal show={showDeleteStudentModal} onHide={() => setShowDeleteStudentModal(false)} centered>
						<Modal.Body>
							<h2 className="m-0 text-shades-800">
								<strong>Delete student</strong>
							</h2>
							<p className="m-0">
								This will permanently delete this student from the organisation.
								<br />
								<br />
								Are you sure you want to continue?
							</p>
							<div className="button-group d-flex">
								<button onClick={() => setShowDeleteStudentModal(false)} className="btn btn-lg btn-white w-50">
									Cancel
								</button>
								<button onClick={handleDeleteStudent} className="btn btn-lg btn-destructive w-50">
									Delete student
								</button>
							</div>
						</Modal.Body>
					</Modal>
					{/* Assign Books modal */}
					<Modal show={showAssignBookModal} onHide={() => setShowAssignBookModal(false)} centered>
						<Modal.Body>
							<button
								type="button"
								className="btn-close close"
								aria-label="Close"
								onClick={() => setShowAssignBookModal(false)}
							></button>
							<h2 className="text-shades-800">
								<strong>Assign Books from Stage</strong>
							</h2>
							<div className="d-flex gap-3 flex-wrap">
								{bookStages?.map((stage) => {
									return (
										<button
											key={stage.id}
											className={`pill stage-${stage.name} dark stage-pill`}
											onClick={() => {
												setSelectedBookStageId(stage.id || '');
												setShowAssignBookModal(false);
												setShowConfirmAssignBookModal(true);
											}}
										>
											Stage {stage.name}
										</button>
									);
								})}
							</div>
							<Divider className="milo-blue" />
							<Link
								state={{
									selectedStudents: [studentId],
									classId: profileDetails?.classId,
									studentNames: [
										`${profileDetails?.firstName || ''} ${profileDetails?.lastName || ''}`.trim()
									].filter(Boolean)
								}}
								to={'/student-management/assign-books'}
								className="btn btn-white btn-lg"
							>
								<SvgMask path="/svg/book-az.svg" width={16} height={16} />
								Manage individual books
							</Link>
						</Modal.Body>
					</Modal>
					{/* Assign Books to Specific Stage modal */}
					<Modal
						show={showAssignBookSpecificStageModal}
						onHide={() => setShowAssignBookSpecificStageModal(false)}
						centered
					>
						<Modal.Body>
							<button
								type="button"
								className="btn-close close"
								aria-label="Close"
								onClick={() => setShowAssignBookSpecificStageModal(false)}
							></button>
							<h2 className="text-shades-800">
								<strong>Assign Stage {activeStageName}</strong>
							</h2>

							<button
								className={`btn btn-white btn-lg`}
								onClick={() => {
									setSelectedBookStageId(bookStages?.find((stage) => stage.name === activeStageName)?.id || '');
									setShowAssignBookSpecificStageModal(false);
									setShowConfirmAssignBookModal(true);
								}}
							>
								<SvgMask path="/svg/book-az.svg" width={16} height={16} />
								Select all books
							</button>

							<Divider className="milo-blue" />
							<Link
								state={{
									selectedStudents: [studentId],
									classId: profileDetails?.classId,
									studentNames: [
										`${profileDetails?.firstName || ''} ${profileDetails?.lastName || ''}`.trim()
									].filter(Boolean)
								}}
								to={'/student-management/assign-books'}
								className="btn btn-white btn-lg"
							>
								<SvgMask path="/svg/book-az.svg" width={16} height={16} />
								Select individual books
							</Link>
						</Modal.Body>
					</Modal>
					{/* Confirm Assign Books modal */}
					<Modal show={showConfirmAssignBookModal} onHide={() => setShowConfirmAssignBookModal(false)} centered>
						<Modal.Body>
							<h2 className="text-shades-800">
								<strong>Assign books to student?</strong>
							</h2>
							{selectedBookStageBooksCount ? (
								<p>
									You are about to assign{' '}
									<strong>
										{selectedBookStageBooksCount} {selectedBookStageBooksCount === 1 ? 'book' : 'books'}
									</strong>{' '}
									to <strong>1 student</strong> and their eLibrary will be updated accordingly.
								</p>
							) : (
								<p>There are no books available in this stage.</p>
							)}
							<div className="button-group d-flex justify-content-center">
								<button
									onClick={() => {
										setShowConfirmAssignBookModal(false);
									}}
									className="btn btn-lg btn-white w-50"
								>
									Cancel
								</button>
								{selectedBookStageBooksCount && (
									<button onClick={handleAssignBooks} className="btn btn-lg w-50" disabled={isAssigningBooks}>
										Assign books
									</button>
								)}
							</div>
						</Modal.Body>
					</Modal>

					{/* Transfer student off canvas modal */}
					<Offcanvas
						bsPrefix="class-select offcanvas"
						scroll
						show={showChangeClassModal}
						onHide={() => setShowChangeClassModal(false)}
						placement="end"
					>
						<Offcanvas.Header>
							<Offcanvas.Title className="h3 text-shades-800">
								<strong>Transfer student to a different class</strong>
							</Offcanvas.Title>
						</Offcanvas.Header>
						<Offcanvas.Body>
							<div className="form-selection-wrapper">
								{classList?.length ? (
									<div className="form-selection-wrapper-inner">
										{classList
											// don't include the class that's already there
											.filter((classItem) => classItem.id !== profileDetails?.classId)
											.sort((a, b) => {
												const aYear = a.year || '';
												const bYear = b.year || '';
												const aName = a.name || '';
												const bName = b.name || '';
												return aYear.localeCompare(bYear) || aName.localeCompare(bName);
											})
											.map((classItem) => {
												const isSelected = selectedClassId === classItem.id;
												return (
													<button
														key={classItem.id}
														type="button"
														onClick={() => setSelectedClassId(classItem.id || '')}
														className={`btn btn-row-label ${isSelected ? 'selected' : ''}`}
													>
														<strong>{classItem.name}</strong>
														<div className="d-flex align-items-center gap-2">
															<div className="selected-icon-wrapper">
																{isSelected ? <SvgMask path="/svg/check.svg" width={16} height={16} /> : null}
															</div>
														</div>
													</button>
												);
											})}
									</div>
								) : null}
							</div>
							<div className="button-group d-flex gap-3">
								<button
									type="button"
									onClick={() => setShowChangeClassModal(false)}
									className="btn btn-white btn-lg w-100"
								>
									Cancel
								</button>
								<button
									type="button"
									onClick={studentIsArchived ? handleUnarchiveStudent : handleChangeClass}
									disabled={!selectedClassId}
									className="btn btn-lg w-100"
								>
									Change Class
								</button>
							</div>
						</Offcanvas.Body>
					</Offcanvas>

					{/* Unassign Stage modal */}
					<Modal show={showUnassignStageModal} onHide={() => setShowUnassignStageModal(false)} centered>
						<Modal.Body>
							<h2 className="m-0 text-shades-800">
								<strong>Unassign Stage {activeStageDetails?.name || activeStageName} books?</strong>
							</h2>
							<p className="body-normalm-0">
								This will remove all books in <strong>Stage {activeStageDetails?.name || activeStageName}</strong> from
								the student&apos;s eLibrary and <strong>reset progress</strong> for the currently assigned books.
							</p>
							<div className="button-group d-flex gap-2">
								<button
									type="button"
									onClick={() => {
										setShowUnassignStageModal(false);
									}}
									className="btn btn-white btn-lg w-50"
								>
									Cancel
								</button>
								<button
									type="button"
									onClick={handleUnassignStage}
									className="btn btn-destructive btn-lg w-50 text-nowrap"
								>
									Unassign all books in Stage
								</button>
							</div>
						</Modal.Body>
					</Modal>

					{/* Edit Student Name Modal */}
					<Modal
						show={showEditStudentNameModal}
						onHide={() => setShowEditStudentNameModal(false)}
						onExited={() =>
							setEditNameValues({
								firstName: profileDetails?.firstName || '',
								lastName: profileDetails?.lastName || ''
							})
						}
						centered
					>
						<Modal.Body>
							<h2 className="m-0 text-shades-800">
								<strong>Edit student name</strong>
							</h2>

							<Formik initialValues={editNameValues} validate={validateEditName} onSubmit={postEditStudentName}>
								<FormikForm
									onChange={(e: ChangeEvent<HTMLFormElement>) => {
										const { name, value } = e.target;
										setEditNameValues({
											...editNameValues,
											[name]: value
										});
									}}
								>
									<FormCustomError className="text-start my-2" />

									<FormGroupWithError name="firstName" className="text-start mb-3">
										<label htmlFor="firstName" className="form-label">
											First Name
										</label>
										<Field type="text" id="firstName" name="firstName" className="form-control" />
									</FormGroupWithError>
									<FormGroupWithError name="lastName" className="text-start mb-3">
										<label htmlFor="lastName" className="form-label">
											Last Name
										</label>
										<Field type="text" id="lastName" name="lastName" className="form-control" />
									</FormGroupWithError>

									<FormCustomError label="sameName" className="text-start my-2" />

									<div className="d-flex button-group mt-4-5">
										<button
											type="button"
											className="btn btn-white btn-lg w-100"
											onClick={() => setShowEditStudentNameModal(false)}
										>
											Cancel
										</button>
										<button type="submit" className="btn btn-lg w-100" disabled={editNameSubmitting}>
											Save changes
										</button>
									</div>
								</FormikForm>
							</Formik>
						</Modal.Body>
					</Modal>
				</>
			)}
			<DynamicFtux
				config={studentDetailsConfig}
				tutorialType="StudentDetail"
				manualRestartTrigger={manualRestartTrigger}
			/>
		</div>
	);
};

export default Student;
